import { Link } from 'react-router-dom';
import { StatusBadge } from '../common/StatusBadge';
import { useAuth } from '../../contexts/AuthContext';
import type { Project } from '../../types';

interface ProjectCardProps {
  project: Project;
}

export function ProjectCard({ project }: ProjectCardProps) {
  const { user } = useAuth();

  const formattedDate = new Date(project.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  // Generate initiative ID from project ID
  const initiativeId = `INI-${project.id.slice(-4).toUpperCase()}`;

  // Determine if "My Idea" badge should show (only for admin/reviewer viewing their own submission)
  const isPrivilegedUser = user?.role === 'admin' || user?.role === 'reviewer';
  const isOwnSubmission = user && project.submitter_id === user.email;
  const showMyIdeaBadge = isPrivilegedUser && isOwnSubmission;

  return (
    <Link
      to={`/project/${project.id}`}
      className="block bg-wolf-900/30 border border-wolf-700/50 p-6 transition-all duration-300 hover:border-action/50 group"
    >
      <div className="flex items-start justify-between gap-4 mb-4 pb-4 border-b border-wolf-700/30">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <p className="font-mono text-xs text-wolf-500">{initiativeId}</p>
            {showMyIdeaBadge && (
              <span className="px-2 py-0.5 text-xs font-medium bg-action/20 border border-action text-action">
                My Idea
              </span>
            )}
          </div>
          <h3 className="text-lg font-semibold text-white truncate group-hover:text-action transition-colors duration-300">
            {project.title}
          </h3>
          <p className="text-sm text-wolf-500 mt-1">by {project.submitter_name}</p>
        </div>
        <StatusBadge status={project.status} />
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-wolf-500">{formattedDate}</span>
        {project.final_score !== undefined && project.final_score !== null && (
          <span className="font-mono text-action">
            {Math.round(project.final_score)}%
          </span>
        )}
      </div>
    </Link>
  );
}
